/**
 * Installer End-of-Day Wrap Engine
 * 
 * Pre-closeout decision layer that determines whether a job is eligible
 * for the existing Closeout flow or needs continuation/follow-up handling.
 * 
 * This is ADDITIVE — it does NOT replace or duplicate the Closeout workflow.
 */

import type { Job } from "./types";

// ── Wrap Outcome Types ──

export type InstallOutcome =
  | "completed_today"
  | "partial_returning"
  | "partial_reschedule"
  | "blocked_issue"
  | "customer_delay";

export type SettlementStatus =
  | "due_now"
  | "deferred_until_completion"
  | "continuation_required"
  | "hold_issue"
  | "collected";

export interface CustomerCommunication {
  customerInformed: boolean;
  rescheduleNeeded: boolean;
  communicationSummary: string;
  proposedNextDate?: string;
}

export type PaymentDisposition = "collected" | "deferred";
export type DeferralReason =
  | "customer_unavailable"
  | "financing_delay"
  | "disputed_amount"
  | "partial_work"
  | "other";

export interface PaymentCapture {
  disposition: PaymentDisposition;
  deferralReason?: DeferralReason;
  deferralNotes?: string;
  followUpAction?: "follow_up_scheduled" | "return_visit_required";
}

export interface ServicePreventionChecklist {
  visualInspection: boolean;
  functionalityConfirmed: boolean;
  materialsInstalledCorrectly: boolean;
  customerWalkthrough: boolean;
  overridden?: boolean;
  overrideReason?: string;
}

export interface InstallerWrapRecord {
  id: string;
  jobId: string;
  installerId: string;
  timestamp: string;
  outcome: InstallOutcome;
  progressNotes: string;
  anotherDayNeeded: boolean;
  customerComm: CustomerCommunication;
  settlementStatus: SettlementStatus;
  schedulingFollowUpRequired: boolean;
  closeoutEligible: boolean;
  /** Estimated completion percentage for partial installs */
  completionPercent: number;
  /** Payment disposition for post-install balances */
  paymentCapture?: PaymentCapture;
  /** Service call prevention checklist (completed installs) */
  serviceChecklist?: ServicePreventionChecklist;
}

// ── Outcome Configuration ──

export const OUTCOME_OPTIONS: { value: InstallOutcome; label: string; description: string; icon: string }[] = [
  { value: "completed_today", label: "Completed Today", description: "Install is 100% done — ready for QA", icon: "check-circle" },
  { value: "partial_returning", label: "Partial — Returning Tomorrow", description: "Work continues next business day", icon: "clock" },
  { value: "partial_reschedule", label: "Partial — Reschedule Needed", description: "Cannot return tomorrow — scheduling required", icon: "calendar" },
  { value: "blocked_issue", label: "Blocked by Issue", description: "Cannot proceed — issue must be resolved first", icon: "alert-triangle" },
  { value: "customer_delay", label: "Customer Delay / Deferral", description: "Customer requested pause or deferral", icon: "user" },
];

// ── Closeout Eligibility ──

export function isCloseoutEligible(outcome: InstallOutcome): boolean {
  return outcome === "completed_today";
}

// ── Settlement Derivation ──

export function deriveSettlementStatus(outcome: InstallOutcome, job: Job): SettlementStatus {
  if (outcome === "completed_today") {
    // Check if all invoices are paid
    const invoices = job.financials?.invoices ?? [];
    const allPaid = invoices.length > 0 && invoices.every(inv => inv.status === "paid");
    if (allPaid) return "collected";
    return "due_now";
  }
  if (outcome === "blocked_issue") return "hold_issue";
  // partial_returning, partial_reschedule, customer_delay
  return outcome === "customer_delay" ? "deferred_until_completion" : "continuation_required";
}

// ── Scheduling Follow-up Derivation ──

export function requiresSchedulingFollowUp(outcome: InstallOutcome): boolean {
  return outcome === "partial_reschedule" || outcome === "blocked_issue" || outcome === "customer_delay";
}

// ── Wrap Validation ──

export interface WrapValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

export function validateWrap(wrap: Partial<InstallerWrapRecord>): WrapValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!wrap.outcome) errors.push("Install outcome is required");
  if (!wrap.progressNotes?.trim()) errors.push("Progress notes are required");

  // If not completed, customer communication is mandatory
  if (wrap.outcome && wrap.outcome !== "completed_today") {
    if (!wrap.customerComm?.customerInformed) {
      errors.push("Customer must be informed when install is incomplete");
    }
    if (!wrap.customerComm?.communicationSummary?.trim()) {
      errors.push("Communication summary is required for incomplete installs");
    }
    if (wrap.customerComm?.rescheduleNeeded && !wrap.customerComm?.proposedNextDate) {
      errors.push("Proposed next date is needed when reschedule is required");
    }
  }

  // Customer confirmation REQUIRED for completed installs (hard gate, not warning)
  if (wrap.outcome === "completed_today" && !wrap.customerComm?.customerInformed) {
    errors.push("Customer must be informed of completion and next steps");
  }

  // Payment resolution REQUIRED for completed installs when there's a balance
  if (wrap.outcome === "completed_today" && wrap.paymentCapture) {
    if (wrap.paymentCapture.disposition === "deferred" && !wrap.paymentCapture.deferralReason) {
      errors.push("Deferral reason is required when payment is deferred");
    }
    if (wrap.paymentCapture.disposition === "deferred" && !wrap.paymentCapture.deferralNotes?.trim()) {
      errors.push("Deferral notes are required when payment is deferred");
    }
  }

  // Service checklist warnings for completed installs
  if (wrap.outcome === "completed_today" && wrap.serviceChecklist) {
    const cl = wrap.serviceChecklist;
    const incomplete = !cl.visualInspection || !cl.functionalityConfirmed || !cl.materialsInstalledCorrectly || !cl.customerWalkthrough;
    if (incomplete && !cl.overridden) {
      errors.push("Service prevention checklist must be completed or overridden before submission");
    }
    if (incomplete && cl.overridden && !cl.overrideReason?.trim()) {
      errors.push("Override reason is required for incomplete service checklist");
    }
  }

  return { valid: errors.length === 0, errors, warnings };
}

// ── Completion Validation ──

export interface CompletionValidation {
  canMarkComplete: boolean;
  failures: string[];
}

export function validateCompletion(
  completionPercent: number,
  unresolvedIssues: number,
  hasAfterPhotos: boolean,
  customerInformed: boolean,
  paymentAddressed: boolean,
): CompletionValidation {
  const failures: string[] = [];
  if (completionPercent < 100) failures.push("Completion must be 100%");
  if (unresolvedIssues > 0) failures.push(`${unresolvedIssues} unresolved issue(s) remain`);
  if (!hasAfterPhotos) failures.push("Final photos are required");
  if (!customerInformed) failures.push("Customer must be informed");
  if (!paymentAddressed) failures.push("Payment must be collected or properly deferred");
  return { canMarkComplete: failures.length === 0, failures };
}

export const DEFERRAL_REASONS: { value: DeferralReason; label: string }[] = [
  { value: "customer_unavailable", label: "Customer Unavailable" },
  { value: "financing_delay", label: "Financing Delay" },
  { value: "disputed_amount", label: "Disputed Amount" },
  { value: "partial_work", label: "Partial Work — Balance Adjusted" },
  { value: "other", label: "Other" },
];

// ── Closeout Gate Validator ──

export interface CloseoutGateResult {
  canCloseout: boolean;
  failures: string[];
}

export function validateCloseoutGate(
  job: Job,
  wrapRecord: InstallerWrapRecord,
  unresolvedIssueCount: number,
): CloseoutGateResult {
  const failures: string[] = [];

  if (wrapRecord.completionPercent < 100) failures.push("Completion is not 100%");
  if (unresolvedIssueCount > 0) failures.push(`${unresolvedIssueCount} unresolved issue(s)`);
  if (!wrapRecord.customerComm.customerInformed) failures.push("Customer has not been informed");

  // Payment must be addressed
  const hasPostInstall = job.financials?.paymentPlan?.lineItems.some(
    li => li.dueAt === "post_install" && li.status !== "paid" && li.status !== "waived"
  );
  if (hasPostInstall && !wrapRecord.paymentCapture) {
    failures.push("Payment status has not been addressed");
  }

  return { canCloseout: failures.length === 0, failures };
}

// ── Financial Sync Helper ──

export function applyPaymentCollectionToJob(job: Job): Job {
  if (!job.financials?.paymentPlan) return job;
  
  const updatedLineItems = job.financials.paymentPlan.lineItems.map(li => {
    if (li.dueAt === "post_install" && li.status !== "paid" && li.status !== "waived") {
      return { ...li, status: "paid" as const };
    }
    return li;
  });

  const updatedInvoices = (job.financials.invoices ?? []).map(inv => {
    if (inv.type === "final" && inv.status !== "paid") {
      return { ...inv, status: "paid" as const, paidAt: new Date().toISOString() };
    }
    return inv;
  });

  return {
    ...job,
    financials: {
      ...job.financials,
      paymentPlan: { ...job.financials.paymentPlan, lineItems: updatedLineItems },
      invoices: updatedInvoices,
      payments: [
        ...(job.financials.payments ?? []),
        {
          id: `PAY-FIELD-${Date.now()}`,
          invoiceId: updatedInvoices.find(i => i.type === "final")?.id ?? "unknown",
          amount: updatedLineItems.filter(li => li.dueAt === "post_install").reduce((s, li) => s + li.amount, 0),
          method: "cash" as const,
          timestamp: new Date().toISOString(),
        },
      ],
    },
  };
}

export function markPaymentDeferred(job: Job): Job {
  if (!job.financials?.paymentPlan) return job;
  
  const updatedLineItems = job.financials.paymentPlan.lineItems.map(li => {
    if (li.dueAt === "post_install" && li.status !== "paid" && li.status !== "waived") {
      return { ...li, status: "pending" as const };
    }
    return li;
  });

  return {
    ...job,
    financials: {
      ...job.financials,
      paymentPlan: { ...job.financials.paymentPlan, lineItems: updatedLineItems },
    },
  };
}

// ── Create Wrap Record ──

export function createWrapRecord(
  jobId: string,
  installerId: string,
  outcome: InstallOutcome,
  progressNotes: string,
  customerComm: CustomerCommunication,
  completionPercent: number,
  job: Job,
): InstallerWrapRecord {
  return {
    id: `WRAP-${Date.now().toString(36).toUpperCase()}`,
    jobId,
    installerId,
    timestamp: new Date().toISOString(),
    outcome,
    progressNotes,
    anotherDayNeeded: outcome !== "completed_today",
    customerComm,
    settlementStatus: deriveSettlementStatus(outcome, job),
    schedulingFollowUpRequired: requiresSchedulingFollowUp(outcome),
    closeoutEligible: isCloseoutEligible(outcome),
    completionPercent: outcome === "completed_today" ? 100 : completionPercent,
  };
}

// ── Day Completion Gate ──

export function hasWrapForToday(
  jobId: string,
  installerId: string,
  records: InstallerWrapRecord[],
): boolean {
  const todayStr = new Date().toISOString().slice(0, 10);
  return records.some(
    r => r.jobId === jobId && r.installerId === installerId && r.timestamp.slice(0, 10) === todayStr,
  );
}

// ── Settlement Display Helpers ──

export const SETTLEMENT_LABELS: Record<SettlementStatus, { label: string; color: string }> = {
  due_now: { label: "Settlement Due Now", color: "text-primary" },
  deferred_until_completion: { label: "Deferred Until Completion", color: "text-muted-foreground" },
  continuation_required: { label: "Continuation Required", color: "text-warning" },
  hold_issue: { label: "On Hold — Issue", color: "text-destructive" },
  collected: { label: "Collected", color: "text-success" },
};
